import { useEffect, useState } from 'react';
import { Moon, Sun, Sunrise, Sunset, type LucideIcon } from 'lucide-react';
import type { HomeTimeBand } from './types';

/**
 * The greeting follows the clock and nothing else — never a deadline, never
 * a workload. The glyph follows the greeting, and it never animates.
 */

/** Bands drive the glyph. Morning and afternoon share the `day` sun. */
export function resolveTimeBand(hour: number): HomeTimeBand {
  if (hour >= 5 && hour < 8) return 'dawn';
  if (hour >= 8 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'dusk';
  return 'night';
}

/** The greeting splits `day` further, so morning and afternoon read right. */
export function resolveGreeting(hour: number): string {
  if (hour >= 5 && hour < 8) return 'Early start';
  if (hour >= 8 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Still going';
}

export const TIME_BAND_ICON: Record<HomeTimeBand, LucideIcon> = {
  dawn: Sunrise,
  day: Sun,
  dusk: Sunset,
  night: Moon,
};

/** Token-backed hues — warm through the day, indigo once it's dark. */
export const TIME_BAND_ICON_CLASS: Record<HomeTimeBand, string> = {
  dawn: 'text-warning',
  day: 'text-orange',
  dusk: 'text-destructive',
  night: 'text-indigo-500',
};

interface TimeOfDay {
  hour: number;
  band: HomeTimeBand;
  /** Greeting for the current hour, without the person's name. */
  greeting: string;
}

/**
 * Re-reads the clock every minute so the greeting rolls over on a screen
 * that has been left open, exactly like the planner header.
 */
export function useTimeOfDay(): TimeOfDay {
  const [hour, setHour] = useState(() => new Date().getHours());

  useEffect(() => {
    const id = setInterval(() => setHour(new Date().getHours()), 60_000);
    return () => clearInterval(id);
  }, []);

  return { hour, band: resolveTimeBand(hour), greeting: resolveGreeting(hour) };
}
